import * as React from 'react';

import {EmptyAddCard} from 'shells/card/empty-add-card';
import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
import {LoadingPage} from 'shells/loading-page/loading-page';

import type {NewsletterStubFragment as Newsletter} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {EditionListQuery} from '../graphql-wrappers/edition/edition-list-query';
import {EditionMutations} from '../graphql-wrappers/edition/edition-mutations';

import {EmptyState} from './empty-state';
import {NewsletterListItem} from './newsletter-list-item';

import styles from './newsletters-board-view.module.css';

type Props = {
    newsletters: Newsletter[];
    isLoading: boolean;
    isFiltering: boolean;
    isShowingArchived: boolean;
    onDeleteNewsletter: (newsletterId: string) => Promise<any>;
    onRestoreNewsletter: (newsletterId: string) => Promise<any>;
    onCreateNewsletter: () => void;
    setIsAddingNewNewsletter: (arg1: boolean) => void;
};

export function NewslettersBoardView(props: Props) {
    if (props.isLoading) {
        return <LoadingPage />;
    }

    if (props.newsletters.length === 0 && (props.isFiltering || props.isShowingArchived)) {
        return (
            <NothingFoundEmptyState
                title='No newsletters were found'
                subtitle='Try loosening your search!'
            />
        );
    }

    if (props.newsletters.length === 0 && !props.isLoading) {
        return <EmptyState onPrimaryCtaClick={() => props.setIsAddingNewNewsletter(true)} />;
    }

    return (
        <React.Fragment>
            {props.newsletters.map((newsletter: Newsletter): React.ReactElement => {
                const isArchived = Boolean(newsletter.deletedTime);

                return (
                    <EditionListQuery newsletterId={newsletter.id} key={newsletter.id}>
                        {({editions, isLoading, error}) => (
                            <EditionMutations>
                                {({onCreate, onDuplicate, onDelete}) => (
                                    <NewsletterListItem
                                        newsletter={newsletter}
                                        editions={editions}
                                        isArchived={Boolean(newsletter.deletedTime)}
                                        onDeleteNewsletter={
                                            !isArchived ? props.onDeleteNewsletter : undefined
                                        }
                                        onRestoreNewsletter={
                                            isArchived ? props.onRestoreNewsletter : undefined
                                        }
                                        onDuplicateEdition={(editionId, campaignId, name) =>
                                            onDuplicate(editionId, campaignId, name).then(
                                                (newEdition) => {
                                                    if (newEdition)
                                                        appHistory.push(newEdition.htmlUrlPath);
                                                }
                                            )
                                        }
                                        onDeleteEdition={onDelete}
                                        onCreateEdition={
                                            !isArchived && editions.length === 0
                                                ? (name, campaignId) =>
                                                      onCreate(name, campaignId).then(
                                                          (newEdition) => {
                                                              if (newEdition)
                                                                  appHistory.push(
                                                                      newEdition.htmlUrlPath
                                                                  );
                                                          }
                                                      )
                                                : undefined
                                        }
                                        isLoadingEditions={isLoading}
                                        editionsError={error}
                                    />
                                )}
                            </EditionMutations>
                        )}
                    </EditionListQuery>
                );
            })}
            <div className={styles['create-newsletter']}>
                <EmptyAddCard
                    background='off-white'
                    size='large'
                    text='Create a newsletter'
                    onClick={props.onCreateNewsletter}
                />
            </div>
            {props.newsletters.length === 0 ? (
                <div className={styles['empty-state-container']}>
                    {[...Array(10)].map((_, index) => (
                        // Create this element 10 times by creating an empty
                        // array and mapping over it.

                        <div key={index} className={styles['create-newsletter']}>
                            <div className={styles['empty-state-box']} />
                        </div>
                    ))}
                </div>
            ) : undefined}
        </React.Fragment>
    );
}
